// ============================================
// CANDIDATES PAGE
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Users, Star, MapPin, Briefcase, Heart, Mail, Edit2 } from 'lucide-react';
import { PageHeader, EmptyState, MatchScoreBadge, CandidateFilters, FavoriteNoteModal } from '@/components';
import { 
  useSeafarers, 
  useMatchingCandidates,
  useFavoriteCandidates,
  useAddFavoriteCandidate,
  useRemoveFavoriteCandidate,
  usePagination, 
  useDebounce 
} from '@/hooks';
import { cn } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/candidates/')({
  component: CandidatesPage,
});

function CandidatesPage() {
  const { t } = useTranslation(['panel', 'common', 'enums']);
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [selectedJobId, setSelectedJobId] = useState('');
  const [availableOnly, setAvailableOnly] = useState(false);
  const { page, limit, goToPage } = usePagination(1, 12);
  const debouncedSearch = useDebounce(search, 300);
  
  // Note modal state
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<{
    id: string;
    name: string;
    notes?: string;
    tags?: string[];
  } | null>(null);

  // Fetch seafarers (regular list)
  const { data: seafarersData, isLoading: seafarersLoading } = useSeafarers({ 
    page, 
    limit, 
    search: debouncedSearch || undefined, 
    department: departmentFilter || undefined,
    available: availableOnly || undefined,
  });

  // Fetch matching candidates if job is selected
  const { data: matchingData, isLoading: matchingLoading } = useMatchingCandidates(selectedJobId);

  // Fetch favorite candidates
  const { data: rawFavoritesData } = useFavoriteCandidates();
  
  const addFavoriteMutation = useAddFavoriteCandidate();
  const removeFavoriteMutation = useRemoveFavoriteCandidate();

  // Handle backend response wrapper - support nested { success, data: { data: [], meta } }
  const unwrapped = (() => {
    if (!seafarersData) return { list: [], meta: undefined };
    const raw = seafarersData as any;
    // { success, data: { data: [...], meta } }
    if (raw.data && Array.isArray(raw.data.data)) {
      return { list: raw.data.data, meta: raw.data.meta || raw.meta };
    }
    // { success, data: [...] }
    if (Array.isArray(raw.data)) {
      return { list: raw.data, meta: raw.meta };
    }
    // { data: [...], meta }
    if (Array.isArray(raw)) {
      return { list: raw, meta: undefined };
    }
    // { items: [...] }
    if (Array.isArray(raw.items)) {
      return { list: raw.items, meta: raw.meta };
    }
    return { list: [], meta: undefined };
  })();
  const seafarers = unwrapped.list;
  const meta = unwrapped.meta;
  const matchingCandidates = Array.isArray(matchingData) 
    ? matchingData 
    : (Array.isArray((matchingData as any)?.data) 
        ? (matchingData as any).data 
        : (Array.isArray((matchingData as any)?.items) 
            ? (matchingData as any).items 
            : []));
  const favoritesArray = Array.isArray(rawFavoritesData) 
    ? rawFavoritesData 
    : (Array.isArray((rawFavoritesData as any)?.data) 
        ? (rawFavoritesData as any).data 
        : (Array.isArray((rawFavoritesData as any)?.items) 
            ? (rawFavoritesData as any).items 
            : []));
  const favoriteIds = new Set(favoritesArray.map((f: any) => f.seafarer?.id));

  // Get favorite entry for a seafarer
  const getFavoriteEntry = (seafarerId: string) => 
    favoritesArray.find((f: any) => f.seafarer?.id === seafarerId);

  // Determine which data to display
  const isLoading = selectedJobId ? matchingLoading : seafarersLoading;
  const displayData = selectedJobId 
    ? matchingCandidates.map((m: any) => ({ ...m.seafarer, matchScore: m.matchScore }))
    : seafarers;

  const toggleFavorite = (seafarerId: string) => {
    if (favoriteIds.has(seafarerId)) {
      removeFavoriteMutation.mutate(seafarerId);
    } else {
      addFavoriteMutation.mutate({ seafarerId });
    }
  };

  const openNoteModal = (seafarer: any) => {
    const favoriteEntry = getFavoriteEntry(seafarer.id);
    setSelectedCandidate({
      id: seafarer.id,
      name: `${seafarer.firstName} ${seafarer.lastName}`,
      notes: favoriteEntry?.notes || '',
      tags: favoriteEntry?.tags || [],
    });
    setNoteModalOpen(true);
  };

  const resetFilters = () => {
    setSearch('');
    setDepartmentFilter('');
    setSelectedJobId('');
    setAvailableOnly(false);
    goToPage(1);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('panel:candidates.title')}
        description={t('panel:candidates.pageDescription')}
      />

      {/* Filtreler */}
      <CandidateFilters
        search={search}
        onSearchChange={setSearch}
        department={departmentFilter}
        onDepartmentChange={setDepartmentFilter}
        selectedJobId={selectedJobId}
        onJobChange={setSelectedJobId}
        availableOnly={availableOnly}
        onAvailableChange={setAvailableOnly}
        onReset={resetFilters}
      />

      {/* İş eşleşme bilgisi */}
      {selectedJobId && matchingCandidates.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-700">
            <strong>{matchingCandidates.length}</strong> {t('panel:candidates.matching.count', { count: matchingCandidates.length }).replace(/<[^>]*>/g, '')} {t('panel:candidates.matching.sortedByScore')}
          </p>
        </div>
      )}

      {/* Aday Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-maritime border border-gray-200 p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-full bg-gray-200 animate-pulse" />
                <div className="flex-1">
                  <div className="h-5 w-32 bg-gray-200 rounded animate-pulse mb-2" />
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : displayData.length === 0 ? (
        <EmptyState
          icon={<Users className="w-8 h-8 text-gray-400" />}
          title={t('panel:candidates.empty.noResults')}
          description={
            selectedJobId 
              ? t('panel:candidates.empty.noMatchingDesc')
              : t('panel:candidates.empty.noSearchDesc')
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayData.map((seafarer: any) => (
            <div
              key={seafarer.id}
              className="bg-white rounded-maritime border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              {/* Eşleşme skoru varsa göster */}
              {seafarer.matchScore !== undefined && (
                <div className="mb-3 -mt-1">
                  <MatchScoreBadge score={seafarer.matchScore} size="sm" />
                </div>
              )}

              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-maritime-100 flex items-center justify-center text-xl font-semibold text-maritime-600 flex-shrink-0">
                    {seafarer.firstName?.[0]}{seafarer.lastName?.[0]}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {seafarer.firstName} {seafarer.lastName}
                    </h3>
                    <p className="text-sm text-maritime-600 truncate">
                      {seafarer.currentRank?.name || seafarer.currentRank?.nameEn || t('panel:candidates.card.noPosition')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {favoriteIds.has(seafarer.id) && (
                    <button
                      onClick={() => openNoteModal(seafarer)}
                      className="p-2 text-gray-400 hover:text-maritime-500 rounded-full hover:bg-gray-100"
                      title={t('panel:candidates.card.editNote')}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => toggleFavorite(seafarer.id)}
                    disabled={addFavoriteMutation.isPending || removeFavoriteMutation.isPending}
                    className={cn(
                      'p-2 rounded-full transition-colors',
                      favoriteIds.has(seafarer.id)
                        ? 'text-red-500 bg-red-50 hover:bg-red-100'
                        : 'text-gray-400 hover:text-red-500 hover:bg-gray-100'
                    )}
                  >
                    <Heart className={cn('w-5 h-5', favoriteIds.has(seafarer.id) && 'fill-current')} />
                  </button>
                </div>
              </div>

              {/* Favori etiketleri varsa */}
              {favoriteIds.has(seafarer.id) && (
                (() => {
                  const entry = getFavoriteEntry(seafarer.id);
                  if (Array.isArray(entry?.tags) && entry.tags.length > 0) {
                    return (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {entry.tags.slice(0, 3).map((tag: string) => (
                          <span 
                            key={tag}
                            className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                        {entry.tags.length > 3 && (
                          <span className="px-2 py-0.5 text-gray-500 text-xs">
                            +{entry.tags.length - 3}
                          </span>
                        )}
                      </div>
                    );
                  }
                  return null;
                })()
              )}

              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="truncate">{t(`enums:department.${seafarer.department}`, seafarer.department)}</span>
                </div>
                {seafarer.experienceYears !== undefined && (
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span>{t('panel:candidates.card.yearsExperience', { count: seafarer.experienceYears })}</span>
                  </div>
                )}
                {seafarer.nationality && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{seafarer.nationality}</span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 flex gap-2">
                <Link 
                  to="/candidates/$id" 
                  params={{ id: seafarer.id }}
                  className="btn-secondary flex-1 text-sm py-2 text-center"
                >
                  {t('panel:candidates.card.viewProfile')}
                </Link>
                <button className="btn-primary flex-1 text-sm py-2 inline-flex items-center justify-center gap-1">
                  <Mail className="w-4 h-4" />
                  {t('panel:candidates.card.contact')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sayfalama - sadece iş eşleştirme yokken göster */}
      {!selectedJobId && meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            className="btn-secondary"
            onClick={() => goToPage(page - 1)}
            disabled={page <= 1}
          >
            {t('panel:candidates.pagination.previous')}
          </button>
          <span className="text-sm text-gray-600">
            {t('panel:candidates.pagination.pageOf', { page, total: meta.totalPages })}
          </span>
          <button
            className="btn-secondary"
            onClick={() => goToPage(page + 1)}
            disabled={page >= meta.totalPages}
          >
            {t('panel:candidates.pagination.next')}
          </button>
        </div>
      )}

      {/* Not Modal */}
      {selectedCandidate && (
        <FavoriteNoteModal
          isOpen={noteModalOpen}
          onClose={() => {
            setNoteModalOpen(false);
            setSelectedCandidate(null);
          }}
          seafarerId={selectedCandidate.id}
          seafarerName={selectedCandidate.name}
          initialNotes={selectedCandidate.notes}
          initialTags={selectedCandidate.tags}
        />
      )}
    </div>
  );
}
